/**
 * Recruiter email (flag `recruiter-email-draft`) — pure helpers.
 *
 * The seeker sends a short, grounded note to a job's VERIFIED applications
 * inbox from their own mailbox (never from our domain). This module holds the
 * rules that must be unit-testable without a database: which address is
 * verified, the per-company 30-day cap decision, and the draft assembly.
 */

import {
	emailDomainOf,
	isAutoSendableApplyEmailShape,
} from "@/lib/apply/apply-email-quality";
import { type JobApplyEmailDraft, withApplyRelay } from "@/lib/apply/gmail";

/** PostHog flag: the SOLE switch (ENABLED = on). Read in the UI and the server action. */
export const RECRUITER_EMAIL_DRAFT_FLAG = "recruiter-email-draft";

/** One recruiter email per company per user per this many days. */
export const RECRUITER_EMAIL_CAP_DAYS = 30;
export const RECRUITER_EMAIL_CAP_MS = RECRUITER_EMAIL_CAP_DAYS * 86_400_000;

/**
 * A recruiter email only goes to an address the posting itself gave us: not
 * derived from the company domain (`apply_email_guessed`), well-formed, on an
 * employer host (not an ATS/aggregator), and not a consumer freemail box.
 */
export function isVerifiedRecruiterAddress(
	applyEmail: string | null | undefined,
	guessed: boolean | null | undefined,
): applyEmail is string {
	if (!applyEmail || guessed === true) return false;
	return isAutoSendableApplyEmailShape(applyEmail);
}

export interface RecruiterCapDecision {
	allowed: boolean;
	/** When the most recent counted email stops counting (null if allowed). */
	nextAllowedAt: Date | null;
}

/**
 * Decide the per-company cap from the `occurredAt` of the seeker's earlier
 * recruiter-email receipts for the same company (or the same address domain).
 */
export function evaluateRecruiterEmailCap(
	priorOccurredAt: ReadonlyArray<Date>,
	now: Date = new Date(),
): RecruiterCapDecision {
	const cutoff = now.getTime() - RECRUITER_EMAIL_CAP_MS;
	const inWindow = priorOccurredAt
		.map((d) => d.getTime())
		.filter((t) => t > cutoff);
	if (inWindow.length === 0) return { allowed: true, nextAllowedAt: null };
	return {
		allowed: false,
		nextAllowedAt: new Date(Math.max(...inWindow) + RECRUITER_EMAIL_CAP_MS),
	};
}

/** Case/space-insensitive company key used to match "the same company". */
export function normalizeCompanyKey(
	company: string | null | undefined,
): string {
	return (company ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** The address domain a cap row is also matched on (two jobs, one employer). */
export function recruiterCapDomain(applyEmail: string): string | null {
	return emailDomainOf(applyEmail);
}

export function buildRecruiterEmailSubject(opts: {
	jobTitle: string;
	company?: string | null;
	locale: "id" | "en";
}): string {
	const at = opts.company
		? opts.locale === "en"
			? ` at ${opts.company}`
			: ` di ${opts.company}`
		: "";
	return opts.locale === "en"
		? `Interested in ${opts.jobTitle}${at}`
		: `Tertarik dengan posisi ${opts.jobTitle}${at}`;
}

/**
 * Assemble the compose draft. `bcc` is the existing send-proof alias
 * (`apply+<trackerId>@apply.lokerdollar.com`) so the send is observable.
 * With `ccRelay` (flag `apply-handoff-cc-relay`) the same alias rides the
 * visible Cc line instead, so an employer reply-all is recorded too.
 */
export function buildRecruiterEmailDraft(opts: {
	to: string;
	jobTitle: string;
	company?: string | null;
	locale: "id" | "en";
	body: string;
	bcc: string;
	ccRelay?: boolean;
}): JobApplyEmailDraft {
	return withApplyRelay(
		{
			to: opts.to,
			subject: buildRecruiterEmailSubject(opts),
			body: `${opts.body.trim()}\n`,
		},
		opts.bcc,
		{ cc: opts.ccRelay === true },
	);
}
